<template>
  <div v-if="category">
    <div v-if="hasAds">
      <v-data-table
        class="apartments-guide-table"
        :headers="headers"
        :header-props="{sortByText: $t('Sort by')}"
        :items="ads"
        :items-per-page="500"
        hide-default-footer
        fixed-header
        :disable-pagination="true"
      >
        <template v-slot:item.name="{ item }">
          <div class="d-flex align-center py-1">
            <v-img
              v-if="item.preview"
              :src="item.preview"
              :lazy-src="item.preview"
              max-height="70"
              max-width="100"
            ></v-img>
            <div class="pl-2 mr-2">
              <router-link
                :to="{ name: 'AdForm', params: {
                  id: item.id.toString(),
                  categoryConst: categoryConst,
                }}"
              >
                {{ item.name }}
              </router-link>
            </div>
          </div>
        </template>

        <template v-slot:item.status="{ item }">
          <span v-if="item.active[$i18n.locale] == '0' && item.status == 'listed'">
            {{ $t('AdFormStatus.unlisted') }}
          </span>
          <span v-else>
            {{ $t(isAdActive(item) ? 'AdFormStatus.listed' : `AdFormStatus.${item.status}`) }}
          </span>
        </template>

        <template v-slot:item.detail.instant="{ item }">
          <div class="d-flex align-center" color="white">
            <v-icon
              :color="Boolean(item.detail && item.detail.instant) ? 'green' : 'grey lighten-1'"
              class="mr-1"
            >
              mdi-check-circle
            </v-icon>
            {{ $t(Boolean(item.detail && item.detail.instant) ? 'Yes' : 'No' ) }}
          </div>
        </template>

        <template v-slot:item.calendarUrl="{ item, index }">
          <v-btn
            v-if="['pending', 'pending_fix', 'listed', 'unlisted', 'active'].includes(item.status)"
            :to="{name: 'Calendar', params: {
              categoryConst: categoryConst,
              id: String(item.id)
            }}"
            x-small
            fab
            color="primary"
            class="mr-3 apartments-guide-calendar-btn"
            :class="{ 'apartments-guide-calendar-btn-first': index === 0 }"
            dark
          >
            <v-icon>mdi-calendar</v-icon>
          </v-btn>
        </template>
        <template v-slot:item.downloadUrl="{ item, index }">
          <v-menu
            v-model="openContractMenus[item.id]"
            @input="setContractMenuValue(item.id, $event)"
            offset-y
            v-if="item && item.contract && item.contract[`url_${$i18n.locale}`]"
          >
            <template v-slot:activator="{ on, attrs }">
              <v-btn
                color="primary"
                dark
                icon
                class="apartments-guide-contract-btn"
                v-bind="attrs"
                v-on="on"
              >
                <v-icon>mdi-dots-vertical</v-icon>
              </v-btn>
            </template>
            <a
              :href="downloadContractUrl(item)"
              target="_blank"
              @click="function(event) { handlePdfDownloadClick(event, item) }"
            >
              <v-btn
                class="text-capitalize font-weight-regular apartments-guide-contract-download-btn"
                :class="{ 'apartments-guide-contract-download-btn-first': index === 0 }"
              >
                {{ $t('Download Cooperation Agreement') }}
              </v-btn>
            </a>
          </v-menu>
        </template>

        <template v-slot:item.options="{ item, index }">
          <div class="d-flex align-center">
            <v-menu
              v-model="openActionsMenus[item.id]"
              @input="setActionsMenuValue(item.id, $event)"
              bottom
              right
              offset-y
              v-if="category.id == 1"
            >
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  v-bind="attrs"
                  v-on="on"
                  class="cursor-pointer text-capitalize rounded-xl mr-2 apartments-guide-actions-activator"
                  :class="{ 'apartments-guide-actions-activator-first': index === 0 }"
                  elevation="0"
                  color="black"
                  icon
                >
                  <v-icon>
                    mdi-dots-vertical
                  </v-icon>
                </v-btn>
              </template>
              <v-list dense>
                <v-list-item
                  v-if="isAdActive(item)"
                  class="apartments-guide-deactivate-action"
                  :class="{ 'apartments-guide-deactivate-action-first': index === 0 }"
                  @click="showApartmentDeactivateModal = true; deactivateApartmentId = item.id"
                >
                  <v-list-item-icon class="mr-2">
                    <v-icon>mdi-eye-off</v-icon>
                  </v-list-item-icon>
                  <v-list-item-title>
                    {{ $t('Deactivate accommodation') }}
                  </v-list-item-title>
                </v-list-item>

                <v-list-item
                  class="apartments-guide-delete-action"
                  :class="{ 'apartments-guide-delete-action-first': index === 0 }"
                  @click="showApartmentDeleteModal = true; deleteApartmentId = item.id"
                >
                  <v-list-item-icon class="mr-2">
                    <v-icon color="red">mdi-delete</v-icon>
                  </v-list-item-icon>
                  <v-list-item-title>
                    {{ $t('Delete accommodation') }}
                  </v-list-item-title>
                </v-list-item>
              </v-list>
            </v-menu>

            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  x-small
                  fab
                  color="primary"
                  v-bind="attrs"
                  v-on="on"
                  class="apartments-guide-edit-btn"
                  :class="{ 'apartments-guide-edit-btn-first': index === 0 }"
                  :to="{ name: 'AdForm', params: {
                    id: item.id.toString(),
                    categoryConst: categoryConst,
                  }}"
                >
                  <v-icon>mdi-pencil-outline</v-icon>
                </v-btn>
              </template>
              <span>{{$t('Edit')}}</span>
            </v-tooltip>

            <v-tooltip bottom v-if="getAdSinglePagePreviewUrl(item)">
              <template v-slot:activator="{ on, attrs }">
                <a
                  :href="getAdSinglePagePreviewUrl(item)"
                  target="_blank"
                >
                  <v-btn
                    x-small
                    fab
                    class="ml-2 apartments-guide-view-btn"
                    :class="{ 'apartments-guide-view-btn-first': index === 0 }"
                    color="primary"
                    v-bind="attrs"
                    v-on="on"
                  >
                    <v-icon>mdi-eye-outline</v-icon>
                  </v-btn>
                </a>
              </template>
              <span>{{$t('View')}}</span>
            </v-tooltip>
          </div>
        </template>
      </v-data-table>

      <accommodation-deactivate-modal
        :show="showApartmentDeactivateModal"
        :apartment-id="deactivateApartmentId"
        @close="showApartmentDeactivateModal = false; deactivateApartmentId = undefined"
      />

      <accommodation-delete-modal
        :show="showApartmentDeleteModal"
        :apartment-id="deleteApartmentId"
        @close="showApartmentDeleteModal = false; deleteApartmentId = undefined"
      />
    </div>
    <div v-else>
      You have no listing, plese add your listing.
      <br/>
      <br/>
      <router-link
        v-if="categoryConst"
        :to="`/listings/${categoryConst}/create`"
        class="text-decoration-none"
      >
        {{ $t('Create Ad') }}
      </router-link>
    </div>
  </div>
</template>

<script>
import ApartmentMixin from '@/lib/ApartmentMixin';
import AccommodationDeactivateModal from '@/components/AccommodationDeactivateModal.vue';
import AccommodationDeleteModal from '@/components/AccommodationDeleteModal.vue';

export default {
  mixins: [ApartmentMixin],
  components: {
    AccommodationDeactivateModal,
    AccommodationDeleteModal,
  },
  props: {
    categoryConst: {
      required: true,
      type: String,
    },
    limit: {
      type: Number,
      default: null,
    },
  },
  data() {
    return {
      ads: [],
      category: undefined,
      showApartmentDeactivateModal: false,
      deactivateApartmentId: undefined,
      showApartmentDeleteModal: false,
      deleteApartmentId: undefined,
      openActionsMenus: {},
      openContractMenus: {},
    };
  },
  watch: {
    categoryConst() {
      this.load();
    },
    limit() {
      this.load();
    },
  },
  computed: {
    headers() {
      let headers = [];
      if (this.category.id === 1) {
        headers = [
          {
            text: this.$t('Name'),
            align: 'start',
            sortable: true,
            value: 'name',
          },
          { text: 'Status', value: 'status' },
          { text: this.$t('Instant reservation'), value: 'detail.instant' },
          { text: this.$t('Calendar'), value: 'calendarUrl' },
          { text: this.$t('Contract'), value: 'downloadUrl' },
          { text: this.$t('Options'), value: 'options' },
        ];
      } else {
        headers = [
          {
            text: this.$t('Name'),
            align: 'start',
            sortable: true,
            value: 'name',
          },
          { text: 'Status', value: 'status' },
          { text: this.$t('Instant reservation'), value: 'detail.instant' },
          { text: this.$t('Contract'), value: 'downloadUrl' },
          { text: this.$t('Options'), value: 'options' },
        ];
      }
      return headers;
    },
    hasAds() {
      return this.ads.length !== 0;
    },
  },
  mounted() {
    window.addEventListener('onboarding:apartments-guide-open-actions-menu', this.openGuideActionsMenuForFirstRow);
    window.addEventListener('onboarding:apartments-guide-close-actions-menu', this.closeGuideActionsMenuForFirstRow);
    window.addEventListener('onboarding:apartments-guide-open-contract-menu', this.openGuideContractMenuForFirstRowWithContract);
    window.addEventListener('onboarding:apartments-guide-close-contract-menu', this.closeGuideContractMenuForFirstRowWithContract);
    this.load();
  },
  beforeDestroy() {
    window.removeEventListener('onboarding:apartments-guide-open-actions-menu', this.openGuideActionsMenuForFirstRow);
    window.removeEventListener('onboarding:apartments-guide-close-actions-menu', this.closeGuideActionsMenuForFirstRow);
    window.removeEventListener('onboarding:apartments-guide-open-contract-menu', this.openGuideContractMenuForFirstRowWithContract);
    window.removeEventListener('onboarding:apartments-guide-close-contract-menu', this.closeGuideContractMenuForFirstRowWithContract);
  },
  methods: {
    load() {
      this.$store.dispatch('loading', true);
      this.$store
        .dispatch('getAdCategoryByConst', this.categoryConst)
        .then((category) => {
          if (!category) {
            this.$router.push({ name: '404' });
            this.$store.dispatch('loading', false);
            return;
          }
          this.category = category;
          this.$emit('category-loaded', category);
        });

      this.$store
        .dispatch('getAdsForCategoryConst', {
          categoryConst: this.categoryConst,
          limit: this.limit || null,
        })
        .then((ads) => {
          this.ads = ads;
          this.$emit('ads-loaded', ads);
          this.$store.dispatch('loading', false);
          this.$nextTick(() => {
            this.$store.dispatch('startApartmentsGuideFlow', {
              routeName: this.$route.name,
              categoryConst: this.categoryConst,
              routePath: this.$route.path,
            });
          });
        });
    },
    downloadContractUrl(item) {
      return `${process.env.VUE_APP_APP_URL}/contract-download/ads/${item.id}/${this.$i18n.locale}`;
    },
    handlePdfDownloadClick(e, item) {
      if (!window.cordova_iab) {
        return;
      }
      if (item.contract && item.contract[`url_${this.$i18n.locale}`]) {
        e.preventDefault();
        const S3Url = 'https://bookaweb.s3.eu-central-1.amazonaws.com';
        const contractUrl = `${S3Url}/${item.contract[`url_${this.$i18n.locale}`]}`;

        window.cordova_iab.postMessage(JSON.stringify({ url: contractUrl, type: 'pdf' }));
      }
    },
    setActionsMenuValue(id, value) {
      this.$set(this.openActionsMenus, id, value);
    },
    setContractMenuValue(id, value) {
      this.$set(this.openContractMenus, id, value);
    },
    openGuideActionsMenuForFirstRow() {
      if (!this.ads.length) {
        return;
      }
      this.setActionsMenuValue(this.ads[0].id, true);
    },
    closeGuideActionsMenuForFirstRow() {
      if (!this.ads.length) {
        return;
      }
      this.setActionsMenuValue(this.ads[0].id, false);
    },
    firstAdWithContractId() {
      const ad = this.ads.find(
        (item) => item && item.contract && item.contract[`url_${this.$i18n.locale}`],
      );
      return ad ? ad.id : null;
    },
    openGuideContractMenuForFirstRowWithContract() {
      const id = this.firstAdWithContractId();
      if (!id) {
        return;
      }
      this.setContractMenuValue(id, true);
    },
    closeGuideContractMenuForFirstRowWithContract() {
      const id = this.firstAdWithContractId();
      if (!id) {
        return;
      }
      this.setContractMenuValue(id, false);
    },
  },
};
</script>
